/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @fileoverview Redux persistence configuration module
 * Configures which parts of the Redux state should be persisted to storage
 * Utilizes redux-persist with custom filtering for optimized storage
 * @module redux/get-persist-config
 */

import get from "lodash.get";
import set from "lodash.set";
import { createTransform } from "redux-persist";

/**
 * Creates a filtered subset of the state object containing only specified paths
 * Used to selectively persist parts of the state tree
 *
 * @function
 * @private
 * @param {Object} state - The complete state object to filter
 * @param {Array<string>} [paths=[]] - Array of path strings to include in the filtered state
 * @returns {Object} Filtered subset of the state containing only the specified paths
 */
function persistFilter(state, paths = []) {
  const subset = {};
  paths.forEach((path) => {
    const value = get(state, path);
    if (typeof value !== "undefined") {
      set(subset, path, value);
    }
  });

  return subset;
}

/**
 * Creates a transform function for selectively persisting parts of a reducer's state
 * Both when saving to storage (inbound) and loading from storage (outbound)
 *
 * @function
 * @private
 * @param {string} reducerName - The name of the reducer to apply this transform to
 * @param {Array<string>} [inboundPaths] - Paths to include when persisting to storage
 * @param {Array<string>} [outboundPaths] - Paths to include when rehydrating from storage
 * @returns {Object} Transform object for redux-persist
 */
function createFilter(reducerName, inboundPaths, outboundPaths) {
  return createTransform(
    (inboundState) => (inboundPaths ? persistFilter(inboundState, inboundPaths) : inboundState),
    (outboundState) => (outboundPaths ? persistFilter(outboundState, outboundPaths) : outboundState),
    {
      whitelist: [reducerName],
    },
  );
}

/**
 * Transform for the authentication reducer state
 * Explicitly specifies which authentication fields should be persisted between sessions
 * Includes user preferences, authentication tokens, and account information
 *
 * @constant
 * @type {Object}
 */
const authReducersFilter = createFilter("auth", [
  "accountId", // User account identifier
  "authToken", // Authentication token for API calls
  "currencyCode", // User's preferred currency
  "desktopView", // Selected desktop view type
  "language", // User's language preference
  "loggedIn", // Whether user is currently logged in
  "isSplitModePreferred", // User preference for split view mode
  "mobileTheme", // Selected theme for mobile view
  "desktopTheme", // Selected theme for desktop view
  "mobileView", // Selected mobile view type
  "playerRequestedDesktopView", // User-requested desktop view preference
  "playerRequestedMobileView", // User-requested mobile view preference
  "playerRequestedPriceFormat", // User-requested odds format
  "priceFormat", // Current odds display format (decimal, fractional, etc.)
  "username", // User's username
  "rememberedUsername", // Remembered username for login screen
  "timezoneOffset", // User's timezone offset for date/time display
]);

/**
 * Transform for the betslip reducer state
 * Persists user's current betslip and jackpot selections between sessions
 * @constant
 * @type {Object}
 */
const betslipReducersFilter = createFilter("betslip", ["betslipData", "jackpotBetslipData"]);

/**
 * Transform for the bet type reducer state
 * Persists bet type definitions and language preferences
 * @constant
 * @type {Object}
 */
const betTypeReducersFilter = createFilter("betType", ["language", "betTypes"]);

/**
 * Transform for the chat reducer state
 * Persists chat session information between page reloads
 * @constant
 * @type {Object}
 */
const chatReducersFilter = createFilter("chat", ["sessionId", "startTime"]);

/**
 * Transform for the cache reducer state
 * Persists initially selected odds for bet comparison
 * @constant
 * @type {Object}
 */
const ezCacheReducersFilter = createFilter("ezCache", ["betslipOutcomeInitialPrices"]);

/**
 * Transform for the live events reducer state
 * Persists user's favorite live events and multi-view selections
 * @constant
 * @type {Object}
 */
const liveReducersFilter = createFilter("live", ["liveFavourites", "multiViewEventIds"]);

/**
 * Transform for the retail account reducer state
 * Persists selected player account for retail mode
 * @constant
 * @type {Object}
 */
const retailReducersFilter = createFilter("retailAccount", ["selectedPlayerAccountId"]);

/**
 * Creates the complete redux-persist configuration
 * Configures which reducers should be persisted and which fields within each reducer
 *
 * @function
 * @param {Object} options - Configuration options
 * @param {Object} options.storage - Storage engine to use for persistence (e.g., localStorage)
 * @returns {Object} Complete redux-persist configuration object
 * @see https://github.com/rt2zz/redux-persist#persistconfig
 */
export default ({ storage }) => ({
  key: "root", // Root key for the persisted data in storage
  stateReconciler: false, // Disable automatic state reconciliation for performance
  storage, // Storage engine (passed from store.js)
  transforms: [
    // Transform functions to selectively persist state
    authReducersFilter,
    betslipReducersFilter,
    betTypeReducersFilter,
    chatReducersFilter,
    ezCacheReducersFilter,
    liveReducersFilter,
    retailReducersFilter,
  ],
  version: 1, // Current schema version for migration purposes
  whitelist: ["auth", "betslip", "betType", "chat", "ezCache", "live", "retailAccount"], // Only persist these reducers
});
